import { useEffect, useMemo, useRef } from "react";
import { useFormContext, type UseFormSetValue } from "react-hook-form";
import { hasFeature } from "src/generator/hooks/useCopyVal";
import {
  ADD_VALUES_FEATURE,
  ADD_VALUES_RESULT_FEATURE,
  DIFFERENCE_FEATURE,
  diffValues,
  findColIdsByFeatureClass,
  sumValues,
  TODAY_FEATURE,
  YESTERDAY_FEATURE,
} from "src/generator/utils/calcFeatures";
import { getOverlayTargetId } from "src/generator/utils/rowTags";
import { useSelector } from "src/redux/hooks";

interface Props {
  id: string;
  feature?: string | string[];
  className?: string;
  setValue: UseFormSetValue<Record<string, any>>;
  recalcKey?: number; // changes when the cell should be calculated right away
}

const useSourceIds = (
  id: string,
  enabled: boolean,
  features: string[],
  className?: string,
) => {
  const sections = useSelector(state =>
    enabled ? state.generatorSlice.lbentry?.sections : undefined,
  );
  const key = features.join("|");

  return useMemo(
    () =>
      sections
        ? key
            .split("|")
            .map(feature =>
              findColIdsByFeatureClass(sections, feature, className).map(
                sourceId => getOverlayTargetId(id, sourceId) as string,
              ),
            )
        : [],
    [className, id, key, sections],
  );
};

const isEditingField = () => {
  const active = document.activeElement;
  return (
    active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement
  );
};

// Calculates when a source cell is left, so saved values are left alone
const useSourceCalc = (
  { id, setValue, recalcKey }: Props,
  sourceIds: string[],
  compute: (values: Record<string, any>) => string,
) => {
  const { subscribe, getValues } = useFormContext();
  const computeRef = useRef(compute);
  computeRef.current = compute;
  const sourceKey = sourceIds.join("|");
  const lastRecalcKey = useRef(recalcKey);

  useEffect(() => {
    if (lastRecalcKey.current === recalcKey) return;
    lastRecalcKey.current = recalcKey;
    if (sourceKey) setValue(id, computeRef.current(getValues()));
  }, [getValues, id, recalcKey, setValue, sourceKey]);

  useEffect(() => {
    const names = sourceKey ? sourceKey.split("|") : [];
    if (names.length === 0) return;

    let pendingValues: Record<string, any> | null = null;
    const flush = () => {
      if (!pendingValues) return;
      setValue(id, computeRef.current(pendingValues));
      pendingValues = null;
    };
    const unsubscribe = subscribe({
      name: names,
      formState: { values: true },
      callback: ({ values, name }) => {
        if (!name || !names.includes(name)) return;
        pendingValues = values;
        // A value written from elsewhere has no blur to wait for
        if (!isEditingField()) flush();
      },
    });
    document.addEventListener("focusout", flush);

    return () => {
      unsubscribe();
      document.removeEventListener("focusout", flush);
    };
  }, [id, setValue, sourceKey, subscribe]);
};

export const useAddValues = (props: Props) => {
  const { id, feature, className } = props;
  const enabled = hasFeature(feature, ADD_VALUES_RESULT_FEATURE);
  const [sourceIds = []] = useSourceIds(
    id,
    enabled,
    [ADD_VALUES_FEATURE],
    className,
  );

  useSourceCalc(props, sourceIds, values =>
    sumValues(sourceIds.map(sourceId => values[sourceId])),
  );
};

export const useDifference = (props: Props) => {
  const { id, feature, className } = props;
  const enabled = hasFeature(feature, DIFFERENCE_FEATURE);
  const [[yesterdayId] = [], [todayId] = []] = useSourceIds(
    id,
    enabled,
    [YESTERDAY_FEATURE, TODAY_FEATURE],
    className,
  );
  const sourceIds = yesterdayId && todayId ? [yesterdayId, todayId] : [];

  useSourceCalc(props, sourceIds, values =>
    diffValues(values[todayId], values[yesterdayId]),
  );
};
